import React from "react";
import {
  Box,
  Typography,
  TextField,
  IconButton,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import ChatMessage from "./ChatMessage";
import request from "../../../utilities/requests";
import { getSocket } from "../../../utilities/socketService";
import { toast } from "../../../utilities/SnackBarUtils";

const MAX_LENGTH = 1000;
const PAGE = 30;
const NEAR_BOTTOM = 80;

const enterSends = () => {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(pointer: fine)").matches;
};

const newClientId = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const sortByTime = (a, b) => {
  const at = new Date(a.createdAt).getTime();
  const bt = new Date(b.createdAt).getTime();
  if (at !== bt) return at - bt;
  return String(a._id) < String(b._id) ? -1 : 1;
};

const mergeRows = (prev, incoming) => {
  const next = [...prev];
  for (const row of incoming) {
    let index = -1;
    if (row.clientId) {
      index = next.findIndex((m) => m.clientId && m.clientId === row.clientId);
    }
    if (index === -1) {
      index = next.findIndex((m) => String(m._id) === String(row._id));
    }
    if (index === -1) {
      next.push(row);
      continue;
    }
    const existing = next[index];
    next[index] = {
      ...row,
      isMine: row.isMine !== undefined ? row.isMine : existing.isMine,
      canDelete: row.canDelete !== undefined ? row.canDelete : existing.canDelete,
    };
  }
  return next.sort(sortByTime);
};

const ChatTab = ({ leagueEventId, onRead }) => {
  const listRef = React.useRef(null);
  const bottomRef = React.useRef(null);
  const prevHeightRef = React.useRef(null);
  const viewerIdRef = React.useRef(null);

  const [messages, setMessages] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [loadingOlder, setLoadingOlder] = React.useState(false);
  const [hasMore, setHasMore] = React.useState(false);
  const [cursor, setCursor] = React.useState(null);
  const [loadError, setLoadError] = React.useState(false);

  const [canPost, setCanPost] = React.useState(false);
  const [chatDisabled, setChatDisabled] = React.useState(false);
  const [canRead, setCanRead] = React.useState(false);
  const [closed, setClosed] = React.useState(false);

  const [draft, setDraft] = React.useState("");
  const [sending, setSending] = React.useState(false);
  const [newBelow, setNewBelow] = React.useState(false);
  const [deleteTarget, setDeleteTarget] = React.useState(null);
  const [sendOnEnter] = React.useState(enterSends);

  const isNearBottom = () => {
    const el = listRef.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM;
  };

  const scrollToBottom = (behavior = "auto") => {
    bottomRef.current?.scrollIntoView({ block: "end", behavior });
    setNewBelow(false);
  };

  const decorate = React.useCallback((row) => {
    if (!row) return row;
    if (row.isMine !== undefined) return row;
    const mine =
      !!viewerIdRef.current &&
      String(row.authorId) === String(viewerIdRef.current);
    return {
      ...row,
      isMine: mine,
      canDelete: mine && !row.deleted,
    };
  }, []);

  const markRead = React.useCallback(
    async (messageId) => {
      const result = await request("leaguechat/read", "post", {
        leagueEventId,
        messageId: messageId || undefined,
      });
      if (result?.success && onRead) onRead();
    },
    [leagueEventId, onRead]
  );

  const applyPermissions = (result) => {
    if (result.viewerId) viewerIdRef.current = result.viewerId;
    setCanRead(!!result.canRead);
    setCanPost(!!result.canPost);
    setChatDisabled(!!result.chatDisabled);
    setClosed(result.chatEnabled === false);
  };

  const loadLatest = React.useCallback(
    async ({ replace = false } = {}) => {
      const result = await request(
        `leaguechat?leagueEventId=${leagueEventId}&limit=${PAGE}`
      );
      if (!result?.success) {
        toast.error(result?.error || "Unable to load chat");
        setLoadError(true);
        setLoading(false);
        return;
      }
      setLoadError(false);
      applyPermissions(result);

      const rows = result.messages || [];
      if (replace) {
        setMessages(rows);
        setHasMore(!!result.hasMore);
        setCursor(result.nextCursor || null);
      } else {
        setMessages((prev) => mergeRows(prev, rows));
      }
      setLoading(false);

      if (result.canRead) {
        markRead(rows[rows.length - 1]?._id);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [leagueEventId]
  );

  React.useEffect(() => {
    if (leagueEventId) loadLatest({ replace: true });
  }, [leagueEventId, loadLatest]);

  React.useEffect(() => {
    if (!loading && messages.length) scrollToBottom();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  React.useEffect(() => {
    if (!leagueEventId) return undefined;

    let attached = null;
    let intervalId = null;

    const onMessage = (message) => {
      const atBottom = isNearBottom();
      setMessages((prev) => mergeRows(prev, [decorate(message)]));
      markRead(message._id);
      if (atBottom) {
        window.requestAnimationFrame(() => scrollToBottom("smooth"));
      } else {
        setNewBelow(true);
      }
    };

    const onDeleted = (payload) => {
      setMessages((prev) =>
        prev.map((m) =>
          String(m._id) === String(payload._id)
            ? {
                ...m,
                deleted: true,
                text: null,
                canDelete: false,
                deletedReason: payload.deletedReason,
              }
            : m
        )
      );
    };

    const onToggled = (payload) => {
      if (String(payload.leagueEventId) !== String(leagueEventId)) return;
      if (payload.chatEnabled === false) {
        setClosed(true);
        setCanPost(false);
      } else {
        setClosed(false);
        loadLatest();
      }
    };

    const onConnect = () => {
      loadLatest();
    };

    const attach = (s) => {
      attached = s;
      s.on("leagueChat:message", onMessage);
      s.on("leagueChat:deleted", onDeleted);
      s.on("leagueChat:toggled", onToggled);
      s.on("connect", onConnect);
    };

    const existing = getSocket();
    if (existing) {
      attach(existing);
    } else {
      intervalId = setInterval(() => {
        const s = getSocket();
        if (s) {
          attach(s);
          clearInterval(intervalId);
          intervalId = null;
        }
      }, 1000);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
      if (attached) {
        attached.off("leagueChat:message", onMessage);
        attached.off("leagueChat:deleted", onDeleted);
        attached.off("leagueChat:toggled", onToggled);
        attached.off("connect", onConnect);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leagueEventId]);

  React.useLayoutEffect(() => {
    const el = listRef.current;
    if (!el || prevHeightRef.current === null) return;
    el.scrollTop = el.scrollHeight - prevHeightRef.current;
    prevHeightRef.current = null;
  }, [messages]);

  const loadOlder = async () => {
    if (!cursor || loadingOlder) return;
    setLoadingOlder(true);
    const result = await request(
      `leaguechat?leagueEventId=${leagueEventId}&limit=${PAGE}&before=${cursor}`
    );
    if (result?.success) {
      prevHeightRef.current = listRef.current?.scrollHeight || 0;
      setMessages((prev) => mergeRows(prev, result.messages || []));
      setHasMore(!!result.hasMore);
      setCursor(result.nextCursor || null);
    } else {
      toast.error(result?.error || "Unable to load older messages");
    }
    setLoadingOlder(false);
  };

  const handleScroll = () => {
    const el = listRef.current;
    if (!el) return;
    if (el.scrollTop < 60 && hasMore && !loadingOlder) loadOlder();
    if (isNearBottom()) setNewBelow(false);
  };

  const deliver = async (text, clientId) => {
    setSending(true);
    const result = await request("leaguechat/post", "post", {
      leagueEventId,
      text,
      clientId,
    });

    if (result?.success) {
      setMessages((prev) => mergeRows(prev, [result.message]));
    } else {
      setMessages((prev) =>
        prev.map((m) =>
          m.clientId === clientId ? { ...m, pending: false, failed: true } : m
        )
      );
      toast.error(result?.error || "Message not sent");
    }
    setSending(false);
  };

  const send = async () => {
    const text = draft.trim();
    if (!text || sending) return;

    const clientId = newClientId();
    setMessages((prev) => [
      ...prev,
      {
        _id: clientId,
        clientId,
        text,
        authorName: "You",
        authorType: "player",
        isMine: true,
        canDelete: false,
        deleted: false,
        pending: true,
        createdAt: new Date().toISOString(),
      },
    ]);
    setDraft("");
    window.requestAnimationFrame(() => scrollToBottom("smooth"));

    await deliver(text, clientId);
  };

  const retry = async (message) => {
    if (sending) return;
    setMessages((prev) =>
      prev.map((m) =>
        m.clientId === message.clientId
          ? { ...m, failed: false, pending: true }
          : m
      )
    );
    await deliver(message.text, message.clientId);
  };

  const discard = (message) => {
    setMessages((prev) => prev.filter((m) => m.clientId !== message.clientId));
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const result = await request("leaguechat/delete", "post", {
      messageId: deleteTarget._id,
    });
    if (result?.success) {
      setMessages((prev) =>
        prev.map((m) =>
          String(m._id) === String(deleteTarget._id)
            ? {
                ...m,
                deleted: true,
                text: null,
                canDelete: false,
                deletedReason: "author",
              }
            : m
        )
      );
    } else {
      toast.error(result?.error || "Could not delete that message");
    }
    setDeleteTarget(null);
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (loadError) {
    return (
      <Box sx={{ px: 2, pt: 3, textAlign: "center" }}>
        <Typography sx={{ fontSize: 14, color: "text.secondary", mb: 2 }}>
          We could not load the chat just now.
        </Typography>
        <Button
          variant="outlined"
          size="small"
          onClick={() => {
            setLoading(true);
            loadLatest({ replace: true });
          }}
        >
          Try again
        </Button>
      </Box>
    );
  }

  if (!canRead) {
    return (
      <Box sx={{ px: 2, pt: 3 }}>
        <Typography sx={{ fontSize: 14, color: "text.secondary" }}>
          Chat is available to players registered for this league.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      <Box
        ref={listRef}
        onScroll={handleScroll}
        sx={{
          height: "calc(100dvh - 300px)",
          minHeight: 220,
          overflowY: "auto",
          overscrollBehavior: "contain",
          px: 2,
          pt: 1,
        }}
      >
        {loadingOlder && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
            <CircularProgress size={20} />
          </Box>
        )}
        {messages.length === 0 ? (
          <Typography sx={{ fontSize: 14, color: "text.secondary", py: 3 }}>
            No messages yet. Say hello to your league.
          </Typography>
        ) : (
          messages.map((message) => (
            <ChatMessage
              key={message.clientId || message._id}
              message={message}
              onDelete={setDeleteTarget}
              onRetry={retry}
              onDiscard={discard}
            />
          ))
        )}
        <div ref={bottomRef} />
      </Box>

      {newBelow && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: -2 }}>
          <Button size="small" variant="contained" onClick={() => scrollToBottom("smooth")}>
            New messages
          </Button>
        </Box>
      )}

      <Box sx={{ px: 2, py: 1.5, borderTop: 1, borderColor: "divider" }}>
        {closed ? (
          <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
            The organizer has turned off chat for this league.
          </Typography>
        ) : !canPost ? (
          <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
            {chatDisabled
              ? "The organizer has turned off your posting in this chat."
              : "You can read this chat but not post to it."}
          </Typography>
        ) : (
          <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1 }}>
            <TextField
              multiline
              maxRows={4}
              size="small"
              fullWidth
              placeholder="Message your league"
              value={draft}
              inputProps={{ maxLength: MAX_LENGTH }}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (!sendOnEnter) return;
                if (e.key !== "Enter" || e.shiftKey) return;
                if (e.nativeEvent?.isComposing) return;
                e.preventDefault();
                send();
              }}
              helperText={
                draft.length > 900
                  ? `${draft.length}/${MAX_LENGTH}`
                  : sendOnEnter
                  ? "Enter to send, Shift + Enter for a new line"
                  : undefined
              }
            />
            <IconButton
              color="primary"
              aria-label="Send message"
              disabled={!draft.trim() || sending}
              onClick={send}
            >
              {sending ? <CircularProgress size={20} /> : <SendIcon />}
            </IconButton>
          </Box>
        )}
      </Box>

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete message</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: 14 }}>
            This removes your message for everyone in the league.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="error" onClick={confirmDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ChatTab;
